import React, { useState, useEffect } from 'react';
import { useExpense } from '../../context/ExpenseContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, Cell 
} from 'recharts';
import { Download, BarChart3, LineChartIcon, FileSpreadsheet } from 'lucide-react';

const CHART_COLORS = [
  '#0F52BA', '#FFC107', '#10B981', '#EF4444', '#8B5CF6', 
  '#F59E0B', '#06B6D4', '#EC4899', '#84CC16', '#6366F1'
];

const PERIOD_LABELS = {
  monthly: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  quarterly: ['Q1', 'Q2', 'Q3', 'Q4'],
  yearly: ['Year']
};

const formatCurrency = (amount, currency = 'INR') => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const ExpenseReports = () => {
  const { lov, getPeriodComparison } = useExpense();
  
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [periodType, setPeriodType] = useState('monthly');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [chartType, setChartType] = useState('bar'); // 'bar' or 'line'
  
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summaryTable, setSummaryTable] = useState([]);
  const [loading, setLoading] = useState(true);

  const years = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    loadReport();
  }, [year, periodType, selectedCategories]);

  const loadReport = async () => {
    setLoading(true);
    
    const categories = selectedCategories.length > 0 ? selectedCategories.join(',') : null;
    const data = await getPeriodComparison(year, periodType, categories);
    
    setReportData(data);
    processChartData(data);
    
    setLoading(false);
  };

  const processChartData = (data) => {
    // Group by period
    const periodMap = {};
    const allCategories = new Set();
    
    data.forEach(item => {
      const period = item._id?.period || 'Year';
      const category = item._id?.category || 'Unknown';
      
      if (!periodMap[period]) {
        periodMap[period] = {};  // Bug 1 Fix: Don't include period property here
      }
      periodMap[period][category] = item.total;
      allCategories.add(category);
    });
    
    // Convert to array and fill missing periods
    const labels = PERIOD_LABELS[periodType];
    const chartArray = labels.map((label, index) => {
      const periodKey = periodType === 'yearly' ? 'Year' : index + 1;
      return {
        period: label,  // Bug 1 Fix: This won't be overwritten now
        ...periodMap[periodKey]
      };
    });
    
    setChartData(chartArray);
    
    // Build summary table
    const categoryTotals = {};
    allCategories.forEach(cat => {
      categoryTotals[cat] = {};
      labels.forEach((label, idx) => {
        const periodKey = periodType === 'yearly' ? 'Year' : idx + 1;
        categoryTotals[cat][label] = periodMap[periodKey]?.[cat] || 0;
      });
      categoryTotals[cat].total = Object.values(categoryTotals[cat]).reduce((a, b) => a + b, 0);
    });
    
    const tableData = Object.entries(categoryTotals).map(([category, periods]) => ({
      category,
      ...periods
    })).sort((a, b) => b.total - a.total);
    
    setSummaryTable(tableData);
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      }
      return [...prev, category];
    });
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === lov.categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories([...lov.categories]);
    }
  };

  const getUniqueCategories = () => {
    return [...new Set(reportData.map(d => d._id?.category).filter(Boolean))];
  };

  const exportReport = () => {
    // Create CSV
    const labels = PERIOD_LABELS[periodType];
    const headers = ['Category', ...labels, 'Total'];
    const rows = summaryTable.map(row => [
      row.category,
      ...labels.map(l => row[l] || 0),
      row.total
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `expense_report_${year}_${periodType}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="space-y-6" data-testid="expense-reports">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Report Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Period Type</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Chart View</Label>
              <div className="flex gap-1">
                <Button 
                  variant={chartType === 'bar' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setChartType('bar')}
                  className="gap-1"
                >
                  <BarChart3 className="w-4 h-4" />
                  Bar
                </Button>
                <Button 
                  variant={chartType === 'line' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setChartType('line')}
                  className="gap-1"
                >
                  <LineChartIcon className="w-4 h-4" />
                  Line
                </Button>
              </div>
            </div>
            
            <Button variant="outline" onClick={exportReport} className="gap-2 ml-auto">
              <Download className="w-4 h-4" />
              Export Report
            </Button>
          </div>
          
          {/* Category Multi-Select */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Filter Categories</Label>
              <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                {selectedCategories.length === lov.categories.length ? 'Clear All' : 'Select All'}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto p-2 border rounded-lg">
              {lov.categories.map(category => (
                <Badge 
                  key={category}
                  variant={selectedCategories.includes(category) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => handleCategoryToggle(category)}
                >
                  {category}
                </Badge>
              ))}
            </div>
            {selectedCategories.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedCategories.length} categories selected
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>
            {periodType.charAt(0).toUpperCase() + periodType.slice(1)} Comparison - {year}
          </CardTitle>
          <CardDescription>
            {chartType === 'bar' ? 'Stacked bar chart' : 'Trend lines'} showing spending by category
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[400px] w-full" />
          ) : chartData.length === 0 || getUniqueCategories().length === 0 ? (
            <div className="h-[400px] flex items-center justify-center text-muted-foreground">
              No data available for the selected period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              {chartType === 'bar' ? (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Legend />
                  {getUniqueCategories().map((category, index) => (
                    <Bar 
                      key={category} 
                      dataKey={category} 
                      stackId="a"
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                    />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Legend />
                  {getUniqueCategories().map((category, index) => (
                    <Line 
                      key={category}
                      type="monotone"
                      dataKey={category}
                      stroke={CHART_COLORS[index % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={{ fill: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle>Summary Table</CardTitle>
          <CardDescription>
            Detailed breakdown by category and period
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : summaryTable.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              No data available
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background">Category</TableHead>
                    {PERIOD_LABELS[periodType].map(label => (
                      <TableHead key={label} className="text-right">{label}</TableHead>
                    ))}
                    <TableHead className="text-right font-bold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaryTable.map((row, index) => (
                    <TableRow key={row.category}>
                      <TableCell className="sticky left-0 bg-background font-medium">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                          />
                          {row.category}
                        </div>
                      </TableCell>
                      {PERIOD_LABELS[periodType].map(label => (
                        <TableCell key={label} className="text-right">
                          {row[label] ? formatCurrency(row[label]) : '-'}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold">
                        {formatCurrency(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell className="sticky left-0 bg-muted/50">Grand Total</TableCell>
                    {PERIOD_LABELS[periodType].map(label => (
                      <TableCell key={label} className="text-right">
                        {formatCurrency(summaryTable.reduce((sum, row) => sum + (row[label] || 0), 0))}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      {formatCurrency(summaryTable.reduce((sum, row) => sum + row.total, 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpenseReports;
